import { useState, useEffect } from 'react';
import { API_BASE } from '../../lib/rpc';

interface UseCrmWebPushOptions {
  slug?: string;
  authInput?: string;
  showToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

export function useCrmWebPush({ slug, authInput, showToast }: UseCrmWebPushOptions) {
  const [pushStatus, setPushStatus] = useState<'granted' | 'default' | 'denied' | 'unsupported'>('default');
  const [isPushRegistered, setIsPushRegistered] = useState(false);
  const [isPushLoading, setIsPushLoading] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        setPushStatus('unsupported');
      } else {
        setPushStatus(Notification.permission as any);
      }
    }
  }, []);

  const subscribeToWebPush = async (silent = false): Promise<boolean> => {
    try {
      if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
        if (!silent) showToast('Web Push is not supported by this browser.', 'error');
        return false;
      }

      const lastSubKey = `last_push_sub_${slug}`;
      const lastSubTime = localStorage.getItem(lastSubKey);
      if (silent && lastSubTime && (Date.now() - parseInt(lastSubTime, 10) < 86400000)) {
        if (Notification.permission === 'granted') {
          setIsPushRegistered(true);
          setPushStatus('granted');
          return true;
        }
      }

      setIsPushLoading(true);

      let permission = Notification.permission;
      if (permission !== 'granted' && !silent) {
        permission = await Notification.requestPermission();
        setPushStatus(permission as any);
      }

      if (permission !== 'granted') {
        if (!silent && permission === 'denied') {
          showToast('Notifications are blocked in browser settings. Please allow them for dark.tel to receive alerts.', 'error');
        }
        setIsPushLoading(false);
        return false;
      }

      await navigator.serviceWorker.register('/sw.js');
      const registration = await navigator.serviceWorker.ready;
      const vapidPublicKey = 'BH1pGQyOkvWjQPjjn3fnaui--TsnXGu6W3KKDZtnf_ur3sTcI7oBvH92d0lpD3gk4RRsm0uhlkb8S79bPBd2WQg';

      const urlB64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        try {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(vapidPublicKey)
          });
        } catch (subErr) {
          console.warn('Initial pushManager.subscribe failed, clearing stale subscription:', subErr);
          const existing = await registration.pushManager.getSubscription();
          if (existing) await existing.unsubscribe();
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8Array(vapidPublicKey)
          });
        }
      }

      if (!subscription) {
        throw new Error('Failed to obtain push subscription from browser push service');
      }

      const agentName = localStorage.getItem(`crm-agent-${slug}`) || localStorage.getItem('agentName') || localStorage.getItem('userEmail') || 'Unknown Agent';
      const deviceOs = navigator.userAgent;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const resolution = `${window.screen.width}x${window.screen.height}`;
      const language = navigator.language;

      const reqUrl = `${API_BASE.replace(/\/$/, '')}/api/wp-tokens/subscribe`;
      const pw = (slug && localStorage.getItem(`crm-pw-${slug}`)) || (slug && sessionStorage.getItem(`crm-pw-${slug}`)) || authInput || '';
      const res = await fetch(reqUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(pw ? { 'x-crm-password': pw } : {})
        },
        body: JSON.stringify({
          crmSlug: slug,
          crmPassword: pw || undefined,
          agentName,
          deviceOs,
          timezone,
          resolution,
          language,
          subscription
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `Server returned HTTP ${res.status}`);
      }

      if (slug) localStorage.setItem(`last_push_sub_${slug}`, String(Date.now()));
      setIsPushRegistered(true);
      setPushStatus('granted');
      if (!silent) showToast('Push notifications successfully registered and active!', 'success');
      return true;
    } catch (e: any) {
      console.error('Error subscribing to push', e);
      if (!silent) {
        const errMsg = e.message || String(e);
        if (errMsg.toLowerCase().includes('push service not available')) {
          showToast('Push service unavailable: Web push is blocked in Incognito/Private mode. If using Brave, enable "Use Google services for push messaging" in brave://settings/privacy.', 'error');
        } else {
          showToast('Failed to subscribe: ' + errMsg, 'error');
        }
      }
      return false;
    } finally {
      setIsPushLoading(false);
    }
  };

  const handleTestPushNotification = async () => {
    try {
      setIsPushLoading(true);
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready.catch(() => null);
        if (reg) await reg.update().catch(() => {});
      }
      const pw = (slug && localStorage.getItem(`crm-pw-${slug}`)) || (slug && sessionStorage.getItem(`crm-pw-${slug}`)) || authInput || '';
      const reqUrl = `${API_BASE.replace(/\/$/, '')}/api/wp-tokens/test`;
      const res = await fetch(reqUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(pw ? { 'x-crm-password': pw } : {})
        },
        body: JSON.stringify({
          crmSlug: slug,
          crmPassword: pw || undefined
        })
      });

      const dataJson = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(dataJson.error || `Server returned HTTP ${res.status}`);
      }

      if (dataJson.sent > 0) {
        showToast(`Test push notification dispatched to ${dataJson.sent} device(s)! Check your notifications.`, 'success');
      } else if (dataJson.total === 0) {
        showToast('No active push tokens registered for this CRM. Tap "Re-sync Notifications" first.', 'error');
      } else {
        showToast('Push notification queued but returned no active listeners. Try re-syncing notifications.', 'error');
      }
    } catch (e: any) {
      showToast('Test Push Failed: ' + (e.message || String(e)), 'error');
    } finally {
      setIsPushLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      subscribeToWebPush(true);
    }
  }, [slug]);

  return {
    pushStatus,
    setPushStatus,
    isPushRegistered,
    isPushLoading,
    subscribeToWebPush,
    handleTestPushNotification
  };
}
